import * as React from 'react'
import { Row, Col } from 'reactstrap'
import { AccentCheckUnbound } from '../../unbound/AccentCheckUnbound';
import { AccentTextUnbound } from '../../unbound/AccentTextUnBound';
import { AccentComboUnbound, AccentMultiComboUnbound } from '../../unbound/AccentComboUnbound';
import { AccentDateUnbound } from '../../unbound/AccentDateUnbound';
import { AccentDateTimeUnbound } from '../../unbound/AccentDateTimeUnbound';
import { AccentNumberUnbound } from '../../unbound/AccentNumberUnbound';
import { AccentSpinner } from '../../AccentSpinner';
import { queryNoTracking } from '../../../services/DataService';

const query_CustomDocument_ParamList = queryNoTracking("CustomDocument_ParamList");
// pdfscript templates (DocumentTemplate row with a CustomizationID): the published definition's params[], same shape
const query_Customization_DocumentParams = queryNoTracking("Customization_DocumentParams");

const normalizeMultiValue = (value) => {
    if (Array.isArray(value)) {
        return value.filter(v => v !== undefined && v !== null);
    }

    if (value === undefined || value === null || value === '') {
        return [];
    }

    return [value];
};

// Layout hints a report author can put at the start of a parameter's Description (stripped before display/sort):
//   '|' = start a new row before this parameter
//   '#' = render this parameter full width
// Both may be combined in either order, e.g. "|#05 Notes".
const ROW_BREAK_MARKER = '|';
const FULL_WIDTH_MARKER = '#';

const parseDescription = (desc) => {
    let label = desc || '';
    let breakBefore = false;
    let fullWidth = false;

    for (;;) {
        if (label.startsWith(ROW_BREAK_MARKER)) { breakBefore = true; label = label.slice(1); }
        else if (label.startsWith(FULL_WIDTH_MARKER)) { fullWidth = true; label = label.slice(1); }
        else break;
    }

    return { label: label.trimStart(), breakBefore, fullWidth };
};

const hasLookupItems = (param) => Array.isArray(param?.CustomDocumentParamaterListItems) && param.CustomDocumentParamaterListItems.length > 0;
const isMultiLookupParam = (param) => !!param?.MultiValue && hasLookupItems(param);

/**
 * Print-time parameters of the selected document template. Every source renders the same declaration shape
 * (CustomDocumentParamater): a DevExpress custom document (customDocumentID / customDocumentVersionID →
 * CustomDocument_ParamList), a pdfscript template (customizationID → Customization_DocumentParams, the
 * published definition's params[]; values reach the script as ctx.params.<Name>), or `declarations` supplied
 * in memory (the template designer's unsaved params[] — nothing is fetched, and the list may be unpublished).
 */
export const CustomParamsCtrl = ({
    customDocumentID,
    customDocumentVersionID,    
    customizationID,
    declarations,
    defaultCustomParamValues,
    onCustomParamsChange
}) => {
    const [params, setParams] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [paramValues, setParamValues] = React.useState({});
    const hasDeclarations = Array.isArray(declarations);
    // the effect re-runs on content, not identity: callers rebuild the array from JSON text on every render
    const declarationsKey = hasDeclarations ? JSON.stringify(declarations) : null;

    React.useEffect(() => {
        if (!hasDeclarations && !customDocumentID && !customizationID) {
            setParams([]);
            setParamValues({});
            return;
        }

        setLoading(true);
        const load = hasDeclarations
            ? Promise.resolve(declarations)
            : customizationID
                ? query_Customization_DocumentParams.getAllWithCache({ customizationID })
                : query_CustomDocument_ParamList.getAllWithCache({ 
                    customDocumentID: customDocumentID, 
                    customDocumentVersionID: customDocumentVersionID 
                });
        load.then(r => {
            if (r && Array.isArray(r)) {
                // Sort by Description (falling back to Name), numeric-aware so "2" precedes "10".
                // Lets report authors control the order with a numeric prefix in the description.
                const sortKey = p => (parseDescription(p.Description).label || p.Name || '').toLowerCase();
                const sortedParams = [...r].sort((a, b) => sortKey(a).localeCompare(sortKey(b), undefined, { numeric: true }));
                
                setParams(sortedParams);
                
                // Initialize param values from defaultCustomParamValues or DefaultValue
                const initialValues = {};
                sortedParams.forEach(param => {
                    const paramName = param.Name;
                    const hasExternalValue = defaultCustomParamValues && Object.prototype.hasOwnProperty.call(defaultCustomParamValues, paramName);

                    if (hasExternalValue) {
                        initialValues[paramName] = isMultiLookupParam(param)
                            ? normalizeMultiValue(defaultCustomParamValues[paramName])
                            : defaultCustomParamValues[paramName];
                    } else if (param.DefaultValue !== undefined && param.DefaultValue !== null) {
                        initialValues[paramName] = isMultiLookupParam(param)
                            ? normalizeMultiValue(param.DefaultValue)
                            : param.DefaultValue;
                    } else if (isMultiLookupParam(param)) {
                        // Required for multi-value DevExpress params when no selection is made.
                        initialValues[paramName] = [];
                    }
                });
                
                setParamValues(initialValues);
                
                // Notify parent of initial values
                if (onCustomParamsChange && Object.keys(initialValues).length > 0) {
                    onCustomParamsChange(initialValues);
                }
            } else {
                setParams([]);
                setParamValues({});
            }
        }).catch(() => {
            // Silently return null on error
            setParams([]);
            setParamValues({});
        }).finally(() => {
            setLoading(false);
        });
    }, [customDocumentID, customDocumentVersionID, customizationID, declarationsKey]);

    const handleParamChange = (paramName, value) => {
        if (paramValues[paramName] === value) return;

        const newValues = {
            ...paramValues,
            [paramName]: value
        };
        setParamValues(newValues);
        
        if (onCustomParamsChange) {
            onCustomParamsChange(newValues);
        }
    };

    // Free-text params commit on blur/Enter rather than every keystroke, as each commit regenerates the report.
    const renderTextParam = (paramName, paramDesc, defaultValue) => (
        <AccentTextUnbound
            defaultValue={defaultValue}
            label={paramDesc}
            onBlur={(value) => handleParamChange(paramName, value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
        />
    );

    // Returns the bare input control for a parameter; grid placement is handled by renderParam.
    const renderParamControl = (param, paramDesc) => {
        const paramName = param.Name;
        const paramType = param.Type || '';
        const defaultValue = paramValues[paramName] !== undefined 
            ? paramValues[paramName] 
            : (param.DefaultValue !== undefined && param.DefaultValue !== null ? param.DefaultValue : null);

        // Render dropdown if list items exist
        if (hasLookupItems(param)) {
            const dropdownData = param.CustomDocumentParamaterListItems.map(item => ({
                ID: item.ID,
                Description: item.Description
            }));

            if (isMultiLookupParam(param)) {
                return (
                    <AccentMultiComboUnbound
                        data={dropdownData}
                        valueField="ID"
                        displayField="Description"
                        defaultValue={normalizeMultiValue(defaultValue)}
                        label={paramDesc}
                        checkedListMode={true}
                        commitOnClose={true}
                        showSelectedCheckIcon={true}
                        onChange={(value) => handleParamChange(paramName, value || [])}
                    />
                );
            }

            return (
                <AccentComboUnbound
                    data={dropdownData}
                    valueField="ID"
                    displayField="Description"
                    defaultValue={defaultValue}
                    label={paramDesc}
                    onChange={(value) => handleParamChange(paramName, value)}
                />
            );
        }

        // Render based on parameter type
        switch (paramType) {
            case 'Boolean':
                return (
                    <AccentCheckUnbound
                        tagName={`customParam_${paramName}`}
                        defaultValue={defaultValue || false}
                        onChange={(e) => handleParamChange(paramName, e.checked)}
                        label={paramDesc}
                    />
                );

            case 'String':
            case 'Guid':
                return renderTextParam(paramName, paramDesc, defaultValue);

            case 'Date':
                return (
                    <AccentDateUnbound
                        defaultValue={defaultValue}
                        label={paramDesc}
                        onChange={(value) => handleParamChange(paramName, value)}
                    />
                );

            case 'DateTime':
                return (
                    <AccentDateTimeUnbound
                        defaultValue={defaultValue}
                        label={paramDesc}
                        onChange={(value) => handleParamChange(paramName, value)}
                    />
                );

            case 'Time':
                // Use text input for time since there's no dedicated TimeUnbound component
                return renderTextParam(paramName, paramDesc, defaultValue);

            case 'Int16':
            case 'Int32':
            case 'Int64':
                return (
                    <AccentNumberUnbound
                        defaultValue={defaultValue}
                        label={paramDesc}
                        isInt={true}
                        onChange={(value) => handleParamChange(paramName, value)}
                    />
                );

            case 'Float':
            case 'Double':
            case 'Decimal':
                return (
                    <AccentNumberUnbound
                        defaultValue={defaultValue}
                        label={paramDesc}
                        isInt={false}
                        onChange={(value) => handleParamChange(paramName, value)}
                    />
                );

            default:
                // Fallback to text input for unknown types
                return renderTextParam(paramName, paramDesc, defaultValue);
        }
    };

    // Grid placement: 1 column on phones, 2 on tablets, 4 on desktop; '#' = full width, '|' = new row before.
    const renderParam = (param) => {
        const { label, breakBefore, fullWidth } = parseDescription(param.Description);
        const colProps = fullWidth ? { xs: 12 } : { xs: 12, md: 6, xl: 3 };

        return (
            <React.Fragment key={param.Name}>
                {breakBefore && <div className="w-100" />}
                <Col {...colProps}>
                    {renderParamControl(param, label)}
                </Col>
            </React.Fragment>
        );
    };

    if (!hasDeclarations && !customDocumentID && !customizationID) {
        return null;
    }

    if (loading) {
        return (
            <Row>
                <Col md={12}>
                    <AccentSpinner />
                </Col>
            </Row>
        );
    }

    if (!params || params.length === 0) {
        return null;
    }

    return (
        <Row>
            {params.map(renderParam)}
        </Row>
    );
};
